<template>
	<div
		class="inline-flex h-[22px] items-center rounded-full border px-2 py-0.5 text-sm"
		:class="chipClasses"
		:style="chipStyles"
	>
		<span>{{ title }}</span>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";

	const props = defineProps<{
		title: string;
		/**
		 * Color for the chip background. Supports multiple formats:
		 * - Tailwind classes: 'bg-blue-500', 'bg-red-600', etc.
		 * - CSS color values: '#ff0000', 'rgb(255, 0, 0)', 'hsl(0, 100%, 50%)'
		 * - If not provided, defaults to 'bg-gray-300'
		 */
		color?: string;
	}>();

	// The reason for this component is to allow for BOTH Tailwind background classes or CSS color values to be used

	/**
	 * Determines if the color is a CSS color value (hex, rgb, etc.) that needs inline styling
	 */
	function isCssColor(color: string): boolean {
		return (
			color.startsWith("#") ||
			color.startsWith("rgb") ||
			color.startsWith("hsl")
		);
	}

	/**
	 * Determines if the color is a valid Tailwind background class
	 */
	function isTailwindColor(color: string): boolean {
		return color.startsWith("bg-") && !isCssColor(color);
	}

	/**
	 * Computed classes for the chip styling
	 */
	const chipClasses = computed(() => {
		if (!props.color) {
			return "bg-gray-300 border-gray-300";
		}

		if (isTailwindColor(props.color)) {
			const bgColorClass = props.color
				.split(" ")
				.find((c) => c.startsWith("bg-"));
			if (bgColorClass) {
				const borderColorClass = bgColorClass.replace("bg-", "border-");
				return `${props.color} ${borderColorClass}`;
			}
			return props.color;
		}

		return "";
	});

	/**
	 * Computed inline styles for the chip
	 */
	const chipStyles = computed(() => {
		if (!props.color || isTailwindColor(props.color)) {
			return {};
		}

		return {
			backgroundColor: props.color,
			borderColor: props.color,
		};
	});
</script>
